import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  confirmActivation,
  resolveActivation,
  type ActivationView,
} from "@/services/auth";
import { AequiaMark, Button, ErrorBanner, Input } from "@/components/ui";

type Phase = "loading" | "set-password" | "affiliated" | "invalid";

export default function AccountActivationPage() {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(
    () => params.token ?? search.get("token") ?? "",
    [params.token, search],
  );

  const [phase, setPhase] = useState<Phase>("loading");
  const [view, setView] = useState<ActivationView | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!token) {
      setPhase("invalid");
      setError("El enlace no contiene un token válido.");
      return;
    }
    resolveActivation(token)
      .then((resolved) => {
        if (!active) return;
        setView(resolved);
        if (!resolved.valid) {
          setPhase("invalid");
          setError(
            resolved.expired
              ? "El enlace expiró. Pide al administrador que reenvíe la invitación."
              : resolved.consumed
                ? "Este enlace ya fue utilizado."
                : "Enlace inválido.",
          );
          return;
        }
        setPhase(
          resolved.purpose === "AFFILIATION" ? "affiliated" : "set-password",
        );
      })
      .catch((err) => {
        if (!active) return;
        setPhase("invalid");
        setError(
          err instanceof Error ? err.message : "Enlace inválido o expirado",
        );
      });
    return () => {
      active = false;
    };
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("La confirmación no coincide.");
      return;
    }
    setSubmitting(true);
    try {
      await confirmActivation(token, password, confirm);
      navigate("/login", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo activar la cuenta",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const tenantName = view?.tenantName ?? "tu organización";

  return (
    <div className="min-h-screen bg-bg grid place-items-center p-6">
      <div className="w-full max-w-[420px] rounded-xl border border-border bg-surface p-7 shadow-subtle">
        <AequiaMark size={72} className="mb-6" />

        {phase === "loading" && (
          <p className="text-sm text-muted">Validando tu enlace…</p>
        )}

        {phase === "set-password" && (
          <form onSubmit={submit}>
            <div className="text-[11px] uppercase tracking-eyebrow text-muted font-semibold mb-2">
              Activación de cuenta
            </div>
            <h1 className="font-serif text-3xl font-medium tracking-tightish mb-2">
              Define tu contraseña
            </h1>
            <p className="text-sm text-muted mb-6">
              Te crearon una cuenta en <strong>{tenantName}</strong>. Define una
              contraseña segura para activarla
              {view?.email ? ` (${view.email})` : ""}.
            </p>
            <Input
              label="Nueva contraseña"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
            />
            <Input
              label="Confirmar contraseña"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              required
            />
            {error && (
              <div className="mb-3">
                <ErrorBanner message={error} />
              </div>
            )}
            <Button
              type="submit"
              variant="brand"
              size="lg"
              full
              loading={submitting}
            >
              Activar mi cuenta
            </Button>
          </form>
        )}

        {phase === "affiliated" && (
          <div>
            <div className="text-[11px] uppercase tracking-eyebrow text-muted font-semibold mb-2">
              Nuevo acceso
            </div>
            <h1 className="font-serif text-3xl font-medium tracking-tightish mb-2">
              Te uniste a {tenantName}
            </h1>
            <p className="text-sm text-muted mb-6">
              Tu cuenta ya tiene acceso a <strong>{tenantName}</strong>. Inicia
              sesión con tus credenciales actuales para entrar.
            </p>
            <Button
              variant="brand"
              size="lg"
              full
              onClick={() =>
                navigate(
                  view?.tenantId ? `/login?tenant=${view.tenantId}` : "/login",
                  { replace: true },
                )
              }
            >
              Ir a iniciar sesión
            </Button>
          </div>
        )}

        {phase === "invalid" && (
          <div>
            <h1 className="font-serif text-2xl font-medium tracking-tightish mb-3">
              Enlace no válido
            </h1>
            <ErrorBanner message={error ?? "Enlace inválido o expirado."} />
          </div>
        )}

        <Link
          to="/login"
          className="block text-center text-xs text-muted hover:text-text mt-5"
        >
          Volver al inicio de sesión
        </Link>
      </div>
    </div>
  );
}
